import { z } from 'zod/v4';

import { normalizeImageWidgetArgs } from './image-widget-args.normalization';
import { IMAGE_URL_DESCRIPTION, partialImageWidgetDataSchema } from './partial-image-widget-data.schema';

/**
 * `url` is the one field promoted from optional to REQUIRED on create — everything else has a sane
 * factory default, but entity-core's factory starts at `image: null`, which renders an empty
 * placeholder box the user then has to fill in by hand. Declared via `.extend()` on the shared
 * partial rather than a hand-copied field list, so the two tools' surfaces can't drift.
 */
/**
 * The message a MISSING url gets, rather than zod's `expected string, received undefined`.
 *
 * Observed live 2026-09-01 on "давай добавим картинок": the model sent three `create_image_widget`
 * operations carrying only `alt`, and the whole batch was rejected with a raw type error. Prevention
 * was already written and did not work — `createDescription` says "get one from search_images. Never
 * invent or recall an address" in as many words — so the remaining lever is the failure itself: it
 * has to name the next call, the way the form's `buttonLabel` and the payment form's fields do.
 *
 * A function so it fires only on the absent case; a wrong TYPE keeps zod's own wording, which is
 * already accurate for it.
 */
const MISSING_URL =
    'a new image needs url: the https:// address of the picture. If the user did not give you one, ' +
    'call search_images and pass a url from its result. Do not create the widget without it and do ' +
    'not invent or recall an address — a made-up one renders as a broken image with nothing to show ' +
    'it failed.';

const createImageWidgetDataSchema = partialImageWidgetDataSchema.extend({
    url: z
        .string({ error: (issue) => (issue.input === undefined ? MISSING_URL : undefined) })
        .min(1)
        .describe(IMAGE_URL_DESCRIPTION),
});

const createImageWidgetObjectSchema = z.object({
    columnId: z.string().describe('Id of the column to create the image widget in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the column's widget list (0-based). Omit to append at the end."),
    data: createImageWidgetDataSchema.describe(
        'Image widget data. Only url is required; alignment, sizing, border, visibility and animation all ' +
            'fall back to sane defaults.'
    ),
});

export const createImageWidgetArgsSchema = z.preprocess(normalizeImageWidgetArgs, createImageWidgetObjectSchema);
